import React, {useEffect, useState} from "react";
import {Badge, Button, Col, Container, FormControl, FormSelect, Modal, Row,} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {getActivities, getLocations} from "../../API/msisdnApi";
import ConditionalRender, {Fallback} from "../ConditionalRender";
import {toast} from "react-toastify";
import messageFromError from "../../utils/errorUtils";
import useClaims from "../../Hooks/useClaims";
import {VIEW_MSISDN_LOCATIONS} from "../../Claims";
import {MdRefresh} from "react-icons/md";
import LoadingSpinner from "../Loading/LoadingSpinner";
import {sleep} from "../../utils/utils";
import commonCss from "./css/CommonModal.module.css";
import MsisdnLocationMap from "../Map/MsisdnLocationMap";

const MsisdnLocationsModal = ({ show, onDismiss, msisdn, geofences }) => {
  const { t } = useTranslation();

  const [locations, setLocations] = useState([]);
  const [date, setDate] = useState(new Date());
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [activities, setActivities] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(null);
  
  const { hasClaim, isLoading: claimsLoading } = useClaims();

  async function fetchLocations() {
    if (!hasClaim(VIEW_MSISDN_LOCATIONS))
      return;

    const from = new Date(date);
    from.setHours(0, 0, 0, 0);

    const to = new Date(date);
    to.setHours(23, 59, 59, 999);
    const locationsResponse = await getLocations(
      msisdn.id,
      from.toISOString(),
      to.toISOString()
    );

    const activitiesResponse = await getActivities(msisdn.id, {
      startDate: from.toISOString(),
      endDate: to.toISOString(),
      ignoreUnknown: false,
    });

    const locations = locationsResponse.data;
    const activities = activitiesResponse.data;

    const updatedLocations = locations.map((location) => {
      const locationTimestamp = new Date(location.timestamp);

      const matchingActivity = activities.find((activity) => {
        const startTime = new Date(activity.startDate);
        const endTime = activity.endDate ? new Date(activity.endDate) : new Date();

        return locationTimestamp >= startTime && locationTimestamp <= endTime;
      });

      return { ...location, activityType: matchingActivity ? matchingActivity.mostProbableActivity : null,};
    });

    setLocations(updatedLocations);
    setActivities(activities);
    // setLocationsLoaded(true);
  }

  useEffect(() => {
    if (date && !claimsLoading) {
      try {
        fetchLocations();
      } catch (ex) {
        console.error(ex);
        toast.error(messageFromError(ex));
      }
    }
  }, [date, claimsLoading]);


  const getDateFromTime = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hours,
      minutes
    );
  };

  const today = "today";
  const yesterday = "yesterday";
  const custom = "custom";

  const [presetDate, setPresetDate] = useState(today);

  const handleDateSelectionChange = (e) => {
    const value = e.target.value;
    setShowCustomDate(false);
    setPresetDate(value);

    switch (value) {
      case today: {
        setDate(new Date());
        break;
      }
      case yesterday: {
        const date = new Date();
        date.setDate(date.getDate() - 1);

        setDate(date);
        break;
      }
      default: {
        setShowCustomDate(true);
        break;
      }
    }
  };

  const handleCustomDateChange = (e) => {
    const value = e.target.value;
    setDate(new Date(value));
  };

  function formatDateReverse(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so add 1 and pad with 0 if necessary.
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  async function handleRefresh() {
    setIsRefetching(true);
    try {
      await sleep(100);
      fetchLocations();
    } catch (ex) {
      console.error(ex);
      toast.error(messageFromError(ex));
    } finally {
      setIsRefetching(false);
    }
  }

  const handleLocationClick = (index) => {
    setSelectedLocationIndex(index);
    setSelectedTime(
      locations[index].timestamp ? new Date(locations[index].timestamp) : null
    );
  };

  const goToNextLocation = () => {
    if (
      selectedLocationIndex !== null &&
      selectedLocationIndex < locations.length - 1
    ) {
      setSelectedLocationIndex(selectedLocationIndex + 1);
      const index = selectedLocationIndex + 1;
      setSelectedTime(
        locations[index].timestamp ? new Date(locations[index].timestamp) : null
      );
    }
  };

  const goToPreviousLocation = () => {
    if (selectedLocationIndex !== null && selectedLocationIndex > 0) {
      setSelectedLocationIndex(selectedLocationIndex - 1);
      const index = selectedLocationIndex - 1;

      setSelectedTime(
        locations[index].timestamp ? new Date(locations[index].timestamp) : null
      );
    }
  };

  const [highlightedActivities, setHighlightedActivities] = useState([]);

  useEffect(() => {
    if (!selectedTime) {
      setHighlightedActivities([]);
      return;
    }

    const highlighted = activities.filter((activity) => {
      const startTime = new Date(activity.startDate);
      const endTime = activity.endDate ? new Date(activity.endDate) : new Date();

      return selectedTime >= startTime && selectedTime <= endTime;
    });

    setHighlightedActivities(highlighted);
  }, [selectedTime]);

  return (
    <Modal
      show={show}
      onHide={onDismiss}
      size={"xl"}
      contentClassName={commonCss.modalHeight}
    >
      <Modal.Header closeButton className="modal-header">
        <Modal.Title>
          {t("pages.workers.viewLocationsDialog.title") + " (" + (msisdn.fullName ? msisdn.fullName : msisdn.id) + ")"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={commonCss.modalBody}>
        <Container className={"px-0"}>
          <Row>
            <Col lg={2} md={4} sm={4}>
              <FormSelect
                className={"border border-dark"}
                onChange={handleDateSelectionChange}
                value={presetDate}
              >
                <option value={today}>Today</option>
                <option value={yesterday}>Yesterday</option>
                <option value={custom}>Custom</option>
              </FormSelect>
            </Col>
            <ConditionalRender condition={showCustomDate}>
              <Col md={4} sm={4} xs={6}>
                <FormControl
                  type={"date"}
                  value={formatDateReverse(date)}
                  onChange={handleCustomDateChange}
                  max={formatDateReverse(new Date())}
                />
              </Col>
            </ConditionalRender>
            <Col className={"d-flex align-items-center"}>
              <Badge bg={"danger"} pill className={"p-2"}>
                <span className={"m-1"}>
                  {locations.length}{" "}
                  {t("pages.workers.viewLocationsDialog.locations")}
                </span>
              </Badge>
            </Col>
            <Col className={"d-flex justify-content-end align-items-center"}>
              <div>
                <ConditionalRender condition={!isRefetching}>
                  <Fallback>
                    <LoadingSpinner variant={"dark"} small className={"me-2"} />
                  </Fallback>

                  <Button
                    variant={"dark"}
                    className={"p-1 d-flex"}
                    onClick={handleRefresh}
                  >
                    <MdRefresh />
                  </Button>
                </ConditionalRender>
              </div>
            </Col>
          </Row>
          <hr />
        </Container>
        <Row style={{ height: "100%" }}>
          <Col md={9} sm={12}>
            <MsisdnLocationMap
              geofences={geofences}
              locations={locations}
              onLocationClick={(index) => handleLocationClick(index)}
              selectedLocation={
                selectedLocationIndex !== null
                  ? locations[selectedLocationIndex]
                  : null
              }
            />
          </Col>

          <Col md={3} sm={12}>
            <div
              style={{
                border: "1px solid black",
                padding: "10px",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  maxHeight: "calc(80vh - 140px)",
                }}
              >
                {activities.map((activity, index) => {
                  const startTime = activity.startDate;
                  const endTime = activity.endDate || new Date();

                  const isHighlighted =
                    highlightedActivities.includes(activity);

                  const getDotColor = (activityType) => {
                    if (activityType === "Walking") return "green";
                    if (activityType === "Driving") return "yellow";
                    if (activityType === "Unknown") return "orange";
                    return "red";
                  };

                  return (
                    <div
                      key={index}
                      style={{
                        padding: "5px",
                        backgroundColor: isHighlighted
                          ? "rgba(50, 50, 50, 0.4)"
                          : "white",
                        borderBottom: "1px solid black",
                        cursor: "pointer",
                      }}
                    >
                      {new Date(activity.startDate).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      -
                      {new Date(endTime).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      - {activity.mostProbableActivity} - {"("}{" "}
                      {activity.startConfidence} - {activity.endConfidence}{" "}
                      {")"}
                      <span
                        style={{
                          display: "inline-block",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: getDotColor(activity.mostProbableActivity),
                          marginLeft: "5px",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          color: isHighlighted ? "black" : "grey",
                          marginTop: "2px",
                        }}
                      >
                        {activity.detectedActivities
                          .map((act) => `${act.activity} - ${act.confidence}`)
                          .join(", ")}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>
        </Row>

        {selectedLocationIndex !==
        <div>Click a location to display controls</div> && (
          <div
            className="location-navigation"
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "200px", // Adjust width as needed
            }}
          >
            <Button
              variant="dark"
              disabled={selectedLocationIndex === locations.length - 1}
              onClick={goToNextLocation}
            >
              {"<"}
            </Button>
            <span style={{ minWidth: "80px", textAlign: "center" }}>
              {locations[selectedLocationIndex]?.timestamp
                ? new Date(
                    locations[selectedLocationIndex].timestamp
                  ).toLocaleTimeString("en-US", { hour12: false })
                : "--:--:--"}
            </span>
            <Button
              variant="dark"
              disabled={selectedLocationIndex === 0}
              onClick={goToPreviousLocation}
            >
              {">"}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MsisdnLocationsModal;
